import { useEffect, useRef } from 'react';
import { Room } from 'twilio-video';
import { HOST_IDENTITY } from '../../../constants';

/**
 * Disconnects a guest from an empty room once the host has left, so they are not left in a dead call.
 *
 * The host is never disconnected by this: a guest who drops (closed tab, lost connection, a link
 * opened on a second device) can rejoin while the host is still there, so the host stays until they
 * press End Call. Leaving the call is what ends it for everyone; video-api completes the room when the
 * host disconnects.
 */
export default function useAutoDisconnectOnEmpty(room: Room) {
  // Track whether we've ever had a remote participant
  const hadRemoteParticipant = useRef(false);

  useEffect(() => {
    // Skip if room is not connected, or if we are the host (who ends the call themselves)
    if (!room || room.state !== 'connected' || room.localParticipant?.identity === HOST_IDENTITY) {
      return;
    }

    // Check if we currently have remote participants
    if (room.participants.size > 0) {
      hadRemoteParticipant.current = true;
    }

    const handleParticipantConnected = () => {
      hadRemoteParticipant.current = true;
    };

    const handleParticipantDisconnected = () => {
      // Only auto-disconnect if we previously had a remote participant
      // and now have none. This prevents disconnecting before anyone joins.
      if (hadRemoteParticipant.current && room.participants.size === 0) {
        room.disconnect();
      }
    };

    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);

    return () => {
      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);
    };
  }, [room]);
}
